// Grants and funders (spec §26; ADR 0019). A grant is money BRE asks a
// funder for, under terms the funder sets. It is not a sponsorship (a
// company buying benefits, kept in Booking) and not a donation (a gift,
// which BTownTix takes at checkout). Hub keeps none of the three as money:
// QuickBooks is the system of record (spec §19, rule 3). Everything here
// about amounts, payments and totals is operational tracking, typed by
// staff, and every screen says so.
//
// A funder is an organization with a funder profile, the way a vendor is an
// organization with a vendor profile (ADR 0016).

import { z } from 'zod'

const DAY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
const ID = z.string().min(1).max(100)
const text = (max: number) => z.string().trim().max(max)
const optionalText = (max: number) => text(max).nullable().optional()
const CENTS = z.number().int().min(0).max(100_000_000_000)

// ------------------------------------------------------------------ words

// Said wherever money appears on a grant.
export const GRANT_MONEY_NOTE =
  'Tracking only, not accounting. Actual amounts are in QuickBooks.'
export const GRANT_TOTALS_NOTE = 'Not accounting: actual amounts are in QuickBooks.'
export const GRANT_RECEIVED_NOTE = 'Not a deposit; record the deposit in QuickBooks.'
export const GRANT_QBO_NOTE = 'Not checked against QuickBooks.'
// Grant, sponsorship and donation are three different things (spec §26).
export const FUNDING_DISTINCTION_NOTE =
  'Sponsorships belong in Sponsorships and donations in BTownTix.'

// ---------------------------------------------------------------- funders

export const FUNDER_TYPES = ['foundation', 'government', 'corporate', 'individual', 'other'] as const
export type FunderType = (typeof FUNDER_TYPES)[number]
export const FUNDER_TYPE_LABELS: Record<FunderType, string> = {
  foundation: 'Foundation',
  government: 'Government',
  corporate: 'Corporate giving program',
  individual: 'Individual',
  other: 'Other',
}

export const FUNDER_CONTACT_ROLES = ['program_officer', 'grants_manager', 'executive', 'finance', 'other'] as const
export type FunderContactRole = (typeof FUNDER_CONTACT_ROLES)[number]
export const FUNDER_CONTACT_ROLE_LABELS: Record<FunderContactRole, string> = {
  program_officer: 'Program officer',
  grants_manager: 'Grants manager',
  executive: 'Executive',
  finance: 'Finance',
  other: 'Other',
}

// ----------------------------------------------------------------- grants

export const GRANT_STATUSES = ['prospect', 'researching', 'letter_of_inquiry', 'invited', 'drafting', 'submitted', 'awarded', 'declined', 'withdrawn', 'closed_out'] as const
export type GrantStatus = (typeof GRANT_STATUSES)[number]
export const GRANT_STATUS_LABELS: Record<GrantStatus, string> = {
  prospect: 'Prospect',
  researching: 'Researching',
  letter_of_inquiry: 'Letter of inquiry',
  invited: 'Invited to apply',
  drafting: 'Drafting',
  submitted: 'Submitted',
  awarded: 'Awarded',
  declined: 'Declined',
  withdrawn: 'Withdrawn',
  closed_out: 'Closed out',
}
// Still being worked toward a decision.
export const GRANT_OPEN_STATUSES: readonly GrantStatus[] = ['prospect', 'researching', 'letter_of_inquiry', 'invited', 'drafting', 'submitted']

export const GRANT_RESTRICTIONS = ['unrestricted', 'restricted'] as const
export type GrantRestriction = (typeof GRANT_RESTRICTIONS)[number]

export const GRANT_MATCH_KINDS = ['amount', 'percent'] as const
export type GrantMatchKind = (typeof GRANT_MATCH_KINDS)[number]

export const GRANT_QBO_KINDS = ['class', 'project', 'customer'] as const
export type GrantQboKind = (typeof GRANT_QBO_KINDS)[number]
export const GRANT_QBO_KIND_LABELS: Record<GrantQboKind, string> = { class: 'Class', project: 'Project', customer: 'Customer' }

// Deliverables are requirements on the grant (ADR 0007), each with its own
// due date, owner and reminders; this says which kind each one is.
export const GRANT_DELIVERABLE_KINDS = ['narrative_report', 'financial_report', 'final_report', 'site_visit', 'acknowledgment', 'other'] as const
export type GrantDeliverableKind = (typeof GRANT_DELIVERABLE_KINDS)[number]
export const GRANT_DELIVERABLE_LABELS: Record<GrantDeliverableKind, string> = {
  narrative_report: 'Narrative report',
  financial_report: 'Financial report',
  final_report: 'Final report',
  site_visit: 'Site visit',
  acknowledgment: 'Acknowledgment or credit line',
  other: 'Other',
}

export const GRANT_LINK_TYPES = ['project', 'event'] as const
export type GrantLinkType = (typeof GRANT_LINK_TYPES)[number]

// A grant with no fiscal year typed on it counts in BRE's fiscal year by
// date (./fiscal-calendar, ADR 0050): calendar years through 2025, the short
// year January 1 to June 30, 2026, then July to June.

// ------------------------------------------------------------------ shapes

export type FunderContact = { id: string; personId: string; personName: string; role: FunderContactRole; note: string | null; addedAt: string }

export type FunderProfile = {
  organizationId: string
  name: string
  funderType: FunderType
  givingAreas: string[]
  portalUrl: string | null
  notes: string | null
  contacts: FunderContact[]
  grants: GrantSummary[]
  createdAt: string
  createdByName: string | null
  version: number
  can: { manage: boolean }
}

export type GrantSummary = {
  id: string
  programName: string
  funderOrganizationId: string
  funderName: string
  status: GrantStatus
  ownerPersonId: string | null
  ownerName: string | null
  amountRequestedCents: number | null
  amountAwardedCents: number | null
  submissionDeadline: string | null
  decisionExpectedOn: string | null
  awardStartsOn: string | null
  awardEndsOn: string | null
  fiscalYear: number | null
  restriction: GrantRestriction
  openDeliverables: number
  overdueDeliverables: number
  updatedAt: string
}

export type GrantDeliverable = {
  id: string
  kind: GrantDeliverableKind
  requirementId: string
  title: string
  status: 'open' | 'satisfied' | 'waived' | 'not_applicable' | 'expired'
  dueOn: string | null
  ownerName: string | null
  overdue: boolean
}

export type GrantPayment = {
  id: string
  expectedOn: string
  amountCents: number
  received: boolean
  receivedOn: string | null
  reference: string | null
  note: string | null
  recordedByName: string | null
  receivedByName: string | null
  version: number
}

export type GrantLink = { id: string; targetType: GrantLinkType; targetId: string; label: string | null; addedAt: string }

export type GrantSubmissionApproval = {
  approvalId: string
  status: 'pending' | 'approved' | 'rejected' | 'withdrawn' | 'invalidated'
  createdAt: string
  // The grant's terms changed after this was asked for.
  termsChanged: boolean
}

export type Grant = GrantSummary & {
  description: string | null
  currency: 'USD'
  restrictionPurpose: string | null
  matchRequired: boolean
  matchKind: GrantMatchKind | null
  matchAmountCents: number | null
  // Whole percent of the award, e.g. 50 for one-to-two.
  matchPercent: number | null
  matchDescription: string | null
  matchSources: string | null
  qboReferenceKind: GrantQboKind | null
  qboReference: string | null
  qboReferenceName: string | null
  requiresSubmissionApproval: boolean
  submittedOn: string | null
  decidedOn: string | null
  deliverables: GrantDeliverable[]
  payments: GrantPayment[]
  paymentTotals: { expectedCents: number; receivedCents: number }
  links: GrantLink[]
  submissionApproval: GrantSubmissionApproval | null
  // Whether the grant can be marked submitted now, and if not, why.
  canSubmit: { ok: boolean; reason: string | null }
  createdAt: string
  createdByName: string | null
  version: number
  can: { manage: boolean }
}

export type GrantTotalsRow = { key: string; label: string; count: number; requestedCents: number; awardedCents: number }
export type GrantTotals = { byStatus: GrantTotalsRow[]; byFiscalYear: GrantTotalsRow[]; note: string }

// ------------------------------------------------------------------ bodies

export const FunderProfileBody = z.object({
  organizationId: ID,
  funderType: z.enum(FUNDER_TYPES),
  givingAreas: z.array(text(80).min(1)).max(20).optional(),
  portalUrl: optionalText(500),
  notes: optionalText(4000),
})

export const FunderProfilePatch = z.object({
  funderType: z.enum(FUNDER_TYPES).optional(),
  givingAreas: z.array(text(80).min(1)).max(20).optional(),
  portalUrl: optionalText(500),
  notes: optionalText(4000),
  expectedVersion: z.number().int().min(1),
})

export const FunderContactBody = z.object({ personId: ID, role: z.enum(FUNDER_CONTACT_ROLES), note: optionalText(500) })

const GrantFields = {
  programName: text(200).min(1),
  description: optionalText(4000),
  ownerPersonId: ID.nullable().optional(),
  amountRequestedCents: CENTS.nullable().optional(),
  amountAwardedCents: CENTS.nullable().optional(),
  submissionDeadline: DAY.nullable().optional(),
  decisionExpectedOn: DAY.nullable().optional(),
  awardStartsOn: DAY.nullable().optional(),
  awardEndsOn: DAY.nullable().optional(),
  fiscalYear: z.number().int().min(2000).max(2100).nullable().optional(),
  restriction: z.enum(GRANT_RESTRICTIONS).optional(),
  restrictionPurpose: optionalText(2000),
  matchRequired: z.boolean().optional(),
  matchKind: z.enum(GRANT_MATCH_KINDS).nullable().optional(),
  matchAmountCents: CENTS.nullable().optional(),
  matchPercent: z.number().int().min(1).max(1000).nullable().optional(),
  matchDescription: optionalText(2000),
  matchSources: optionalText(2000),
  requiresSubmissionApproval: z.boolean().optional(),
}

export const GrantCreateBody = z.object({ funderOrganizationId: ID, status: z.enum(GRANT_STATUSES).optional(), ...GrantFields })

export const GrantPatch = z.object({ ...GrantFields, programName: text(200).min(1).optional(), expectedVersion: z.number().int().min(1) })

export const GrantStatusBody = z.object({ status: z.enum(GRANT_STATUSES), note: optionalText(1000), expectedVersion: z.number().int().min(1) })

export const GrantQboBody = z.object({
  qboReferenceKind: z.enum(GRANT_QBO_KINDS).nullable(),
  qboReference: text(100).min(1).nullable(),
  qboReferenceName: optionalText(200),
  expectedVersion: z.number().int().min(1),
})

export const GrantDeliverableBody = z.object({
  kind: z.enum(GRANT_DELIVERABLE_KINDS),
  title: text(300).min(1).optional(),
  description: optionalText(2000),
  dueOn: DAY,
  ownerPersonId: ID.nullable().optional(),
})

export const GrantPaymentBody = z.object({ expectedOn: DAY, amountCents: CENTS.refine((n) => n > 0, 'More than zero'), note: optionalText(500) })

export const GrantPaymentReceivedBody = z.object({
  receivedOn: DAY,
  // A check number or an ACH reference.
  reference: text(100).min(1),
  note: optionalText(500),
  expectedVersion: z.number().int().min(1),
})

export const GrantLinkBody = z.object({ targetType: z.enum(GRANT_LINK_TYPES), targetId: ID })

export const GrantListQuery = z.object({
  status: z.enum(['open', 'all', ...GRANT_STATUSES]).optional(),
  funder: z.string().max(100).optional(),
  owner: z.string().max(100).optional(),
  deadlineFrom: DAY.optional(),
  deadlineTo: DAY.optional(),
  q: z.string().max(100).optional(),
})
export type GrantListParams = z.infer<typeof GrantListQuery>
